import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { positionCreateSchema } from '@rhcst/shared';
import type { PortfolioResponseDto, PositionDto } from '@rhcst/shared';
import { api, ApiRequestError } from '../lib/api.js';
import { shortAddress, formatEth, formatTokenAmount, relativeAge, absoluteTime } from '../lib/format.js';

/**
 * Portfolio.
 *
 * Manual position tracking. Amounts are handled as strings from input to display
 * and never parsed into a float — this is the number the operator reconciles
 * against their wallet, so it has to be exactly what they typed.
 *
 * Closing a position is preferred over deleting it: the record of having held
 * something is worth keeping. Delete exists for a mistyped entry.
 *
 * v2 adds an explicit, honest "Unrealized P&L: not tracked yet" note and
 * table column (invariant 7 — the backend does not compute this either, so
 * this is a placeholder for future wiring, never a fabricated $0.00 or a
 * bare dash that could be misread as zero).
 */

export function Portfolio() {
  const [data, setData] = useState<PortfolioResponseDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showClosed, setShowClosed] = useState(false);

  const [tokenAddress, setTokenAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [costBasis, setCostBasis] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.get<PortfolioResponseDto>('/portfolio');
      setData(result);
      setError(null);
    } catch (err) {
      if (!(err instanceof ApiRequestError && err.isUnauthorized)) {
        setError('Could not load the portfolio.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function addPosition(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    // Validated with the same schema the API enforces, so the browser and the
    // server cannot disagree about what is acceptable.
    const parsed = positionCreateSchema.safeParse({
      token_address: tokenAddress,
      amount,
      cost_basis_eth: costBasis,
      ...(notes ? { notes } : {}),
    });

    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? 'Check the values you entered.');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/portfolio/positions', parsed.data);
      setTokenAddress('');
      setAmount('');
      setCostBasis('');
      setNotes('');
      await load();
    } catch (err) {
      setFormError(err instanceof ApiRequestError ? err.message : 'Could not add the position.');
    } finally {
      setSubmitting(false);
    }
  }

  async function setClosed(position: PositionDto, closed: boolean) {
    setBusyId(position.id);
    try {
      await api.patch(`/portfolio/positions/${position.id}`, { closed });
      await load();
    } catch {
      setError('Could not update the position.');
    } finally {
      setBusyId(null);
    }
  }

  async function remove(position: PositionDto) {
    setBusyId(position.id);
    try {
      await api.delete(`/portfolio/positions/${position.id}`);
      await load();
    } catch {
      setError('Could not delete the position.');
    } finally {
      setBusyId(null);
    }
  }

  const positions = data?.positions ?? [];
  const visible = showClosed ? positions : positions.filter((p) => p.closed_at === null);

  return (
    <div className="shell-content">
      {error !== null && (
        <p className="notice" data-tone="alarm" role="alert">
          {error}
        </p>
      )}

      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">Positions</h2>
          <span className="legend">
            {data
              ? `${data.totals.open_positions} open · ${formatEth(data.totals.open_cost_basis_eth)} ETH cost basis`
              : ''}
          </span>
        </div>

        {loading ? (
          <div className="panel-body">
            <p className="panel-note">Loading portfolio…</p>
          </div>
        ) : visible.length === 0 ? (
          <div className="empty">
            <p className="empty-title">{showClosed ? 'No positions' : 'No open positions'}</p>
            <p className="empty-body">
              Record what you hold using the form below. Positions will also open automatically
              from confirmed trades once hot-wallet execution is enabled.
            </p>
            {!showClosed && data && data.totals.closed_positions > 0 && (
              <p>
                <button className="btn-mini" type="button" onClick={() => setShowClosed(true)}>
                  Show {data.totals.closed_positions} closed
                </button>
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="panel-body" style={{ paddingBottom: 0 }}>
              <p className="pnl-note">
                P&amp;L — not computed yet. Positions are cost-basis records only
                until hot-wallet execution auto-opens them and a current-price
                feed is wired in. The column below is a placeholder for that,
                not a live number.
              </p>
            </div>
            <div className="table-scroll">
              <table className="ptable">
                <thead>
                  <tr>
                    <th scope="col">Opened</th>
                    <th scope="col">Token</th>
                    <th scope="col" style={{ textAlign: 'right' }}>
                      Amount
                    </th>
                    <th scope="col" style={{ textAlign: 'right' }}>
                      Cost basis
                    </th>
                    <th scope="col" style={{ textAlign: 'right' }}>
                      Unrealized P&amp;L
                    </th>
                    <th scope="col">Note</th>
                    <th scope="col">State</th>
                    <th scope="col" style={{ textAlign: 'right' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((position) => (
                    <tr key={position.id}>
                      <td className="ptable-num" title={absoluteTime(position.opened_at)}>
                        {relativeAge(position.opened_at)}
                      </td>
                      <td>
                        <span className="pair-id">
                          {position.pair_id ? (
                            <Link className="pair-symbols" to={`/pairs/${position.pair_id}`}>
                              {position.token?.symbol ?? shortAddress(position.token_address)}
                            </Link>
                          ) : (
                            <span className={position.token?.symbol ? undefined : 'token-unknown'}>
                              {position.token?.symbol ?? shortAddress(position.token_address)}
                            </span>
                          )}
                          <span className="pair-address">
                            {shortAddress(position.token_address, 10, 6)}
                          </span>
                        </span>
                      </td>
                      <td className="ptable-num">{formatTokenAmount(position.amount)}</td>
                      <td className="ptable-num">{formatEth(position.cost_basis_eth)}</td>
                      <td className="ptable-num">
                        <span className="pnl-not-tracked">— not tracked</span>
                      </td>
                      <td>
                        {position.notes ? (
                          position.notes
                        ) : (
                          <span className="token-unknown">—</span>
                        )}
                      </td>
                      <td>
                        <span
                          className="chip"
                          data-tone={position.closed_at === null ? 'tracking' : 'muted'}
                        >
                          {position.closed_at === null ? 'Open' : 'Closed'}
                        </span>
                      </td>
                      <td className="ptable-actions">
                        <button
                          className="btn-mini"
                          type="button"
                          disabled={busyId === position.id}
                          onClick={() => void setClosed(position, position.closed_at === null)}
                        >
                          {position.closed_at === null ? 'Close' : 'Reopen'}
                        </button>{' '}
                        <button
                          className="btn-mini"
                          type="button"
                          disabled={busyId === position.id}
                          onClick={() => void remove(position)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {data && data.totals.closed_positions > 0 && (
              <div className="panel-body">
                <button
                  className="btn-mini"
                  type="button"
                  onClick={() => setShowClosed((current) => !current)}
                >
                  {showClosed ? 'Hide closed' : `Show ${data.totals.closed_positions} closed`}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">Record a position</h2>
        </div>
        <div className="panel-body">
          <form onSubmit={addPosition}>
            <div className="form-grid">
              <label className="filter">
                <span className="filter-label">Token address</span>
                <input
                  className="filter-input"
                  type="text"
                  required
                  spellCheck={false}
                  placeholder="0x…"
                  value={tokenAddress}
                  onChange={(event) => setTokenAddress(event.target.value)}
                />
              </label>

              <label className="filter">
                <span className="filter-label">Amount</span>
                <input
                  className="filter-input"
                  type="text"
                  required
                  inputMode="decimal"
                  placeholder="1000.5"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                />
              </label>

              <label className="filter">
                <span className="filter-label">Cost basis (ETH)</span>
                <input
                  className="filter-input"
                  type="text"
                  required
                  inputMode="decimal"
                  placeholder="0.05"
                  value={costBasis}
                  onChange={(event) => setCostBasis(event.target.value)}
                />
              </label>

              <label className="filter">
                <span className="filter-label">Note</span>
                <input
                  className="filter-input"
                  type="text"
                  maxLength={1000}
                  placeholder="Optional"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </label>
            </div>

            {formError !== null && (
              <p className="notice" data-tone="alarm" role="alert" style={{ marginTop: '1rem' }}>
                {formError}
              </p>
            )}

            <div className="form-actions">
              <button className="btn btn-primary" type="submit" disabled={submitting}>
                {submitting ? 'Adding…' : 'Add position'}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
